// src/features/team/JoinRequestDialog.tsx
// "Request to join" dialog, shown from the team hub header for people who are
// not members of a visible team. Extracted from the retired TeamDetailPage.

import { useEffect, useState } from 'react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  ErrorAlert,
} from '@react/components';

interface JoinRequestDialogProps {
  teamId: string;
  teamName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function JoinRequestDialog({ teamId, teamName, onClose, onSuccess }: JoinRequestDialogProps) {
  const store = useTeam();
  const { t } = useI18n();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.CreateTeamJoinApplication(teamId, {
        application: {
          message: message.trim(),
          team: { id: teamId },
        } as never,
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('team.joinRequest.title')}
    >
      <button
        type="button"
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm cursor-default"
        aria-label={t('common.cancel')}
        onClick={onClose}
      />
      <Card className="relative w-full max-w-md animate-in fade-in zoom-in-95">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{t('team.joinRequest.title')}</CardTitle>
            <CardDescription>{t('team.joinRequest.description', { name: teamName })}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitError != null && (
              <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />
            )}
            <Input
              label={t('team.joinRequest.messageLabel')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('team.joinRequest.messagePlaceholder')}
            />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('team.joinRequest.submit')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
